import { useEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { Button } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  VisualManagement,
  CardNotificationList,
  BigNumberKPIList,
  AdministrativeActionsModal,
  EmployeeActionArea,
} from '../../components'
import {
  useDashboardNotifications,
  useDashboardKPIs,
  useUserPlan,
} from '../../hooks'
import { VisualManagementDrawerProvider } from '../../contexts/VisualManagementDrawerContext'
import {
  AdministrativeActionsModalProvider,
  useAdministrativeActionsModal,
} from '../../contexts/AdministrativeActionsModalContext'
import {
  EmployeeContextProvider,
  useEmployeeContext,
} from '../../contexts/EmployeeContext'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 44px;
  background-color: #f1f3f5;
`

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ActionHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-180deg);
  }
`

const inverseRotate = keyframes`
  from {
    transform: rotate(-180deg);
  }
  to {
    transform: rotate(0deg);
  }
`

const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDownIcon)`
  transition: transform 0.2s ease;
  ${({ animate }) =>
    animate
      ? css`
          animation: ${rotate} 0.6s forwards;
        `
      : css`
          animation: ${inverseRotate} 0.6s forwards;
        `};
`

const DashboardWrapper = ({
  loadingNotifications,
  notifications,
  loadingKPIs,
  kpis,
  planName,
}) => {
  const [toggleNotifications, setToggleNotification] = useState(true)
  const { openModal, setLink } = useAdministrativeActionsModal()
  const { selectedEmployee } = useEmployeeContext()
  const { t } = useTranslation('common')

  return (
    <>
      <ActionContainer>
        <ActionHeaderContainer>
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setToggleNotification(prev => !prev)}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
              }}
            >
            {t('dashboard.recent_notifications')}
            </span>
            <StyledKeyboardArrowDownIcon animate={toggleNotifications} />
          </div>
          <Button
            onClick={openModal}
            variant='outlined'
          >
            {t('dashboard.create_intervention')}
          </Button>
        </ActionHeaderContainer>

        {!loadingNotifications && toggleNotifications && (
          <div style={{ marginTop: 24, marginBottom: 8 }}>
            <CardNotificationList cards={notifications} />
          </div>
        )}
      </ActionContainer>
      <ActionContainer>
        <span
          style={{
            fontSize: 18,
            fontWeight: 600,
            marginTop: 16,
            marginBottom: 24,
          }}
        >
          {t('dashboard.my_teams')}
        </span>
        {!loadingKPIs && <BigNumberKPIList kpis={kpis} />}
      </ActionContainer>
      <VisualManagementDrawerProvider>
        <VisualManagement planName={planName} />
      </VisualManagementDrawerProvider>

      <AdministrativeActionsModal
        enableNotifications
        enableAnnotations
      />

      {selectedEmployee?.name && (
        <EmployeeActionArea
          selectedEmployee={selectedEmployee}
          setLink={setLink}
          openModal={openModal}
        />
      )}
    </>
  )
}

const refreshTime = 1000 * 60 * 10

const Dashboard = () => {
  const { loading: loadingNotifications, data: notifications } =
    useDashboardNotifications()
  const { loading: loadingKPIs, data: kpis } = useDashboardKPIs()
  const { loading: loadingUserPlan, data: userPlan } = useUserPlan()

  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload()
    }, refreshTime)

    return () => clearInterval(interval)
  }, [])

  return (
    <Container>
      <EmployeeContextProvider>
        <AdministrativeActionsModalProvider>
          {!loadingUserPlan && (
            <DashboardWrapper
              loadingNotifications={loadingNotifications}
              notifications={notifications}
              loadingKPIs={loadingKPIs}
              kpis={kpis}
              planName={userPlan.name}
            />
          )}
        </AdministrativeActionsModalProvider>
      </EmployeeContextProvider>
    </Container>
  )
}

export default Dashboard
